/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { Route, Switch } from 'react-router';
import { BlankPage } from 'src/appShell/BlankPage';
import { LoadingPage } from 'src/appShell/LoadingPage';
import { ErrorBoundary } from 'src/core/components/ErrorBoundary';
import { NotFoundRedirect } from 'src/core/components/NotFoundRoute';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { getStylist } from 'src/core/themes/luisStylist';
import { isDevelopmentEnvironment, isStagingEnvironment } from 'src/core/utils/api/environment';
import { getStorage } from 'src/core/utils/getStorage';
import { lazyComponentLoader } from 'src/core/utils/lazyComponent';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { routeTable } from 'src/core/utils/routeTable';

const { styleComponent } = getStylist('Routes');
const AppRoutesContentContainer = styleComponent<IStackProps>(Stack)('AppRoutesContentContainer', { overflow: 'hidden' });

const LuisRoutes = lazyComponentLoader(async () => (await import(/* webpackChunkName: "Luis" */ 'src/luis/LuisRoutes')).LuisRoutes);
const LanguageStudioRoutes = lazyComponentLoader(
    async () => (await import(/* webpackChunkName: "LanguageStudio" */ 'src/languageStudio/LanguageStudioRoutes')).LanguageStudioRoutes
);
const DemosRoutes = lazyComponentLoader(
    async () => (await import(/* webpackChunkName: "InternalDemos" */ 'src/demos/DemosRoutes')).DemosRoutes
);

/**
 * The main routes component that creates the main routes for
 * the website.
 */
export const Routes = observer(() => {
    const { rootStore, uiStore } = useGlobalState();

    const isAuthLoading = rootStore.authStore.isLoading;

    const gaPortalRoutes = [
        isDevelopmentEnvironment() && (
            <Route key="languageStudio" path={routeTable.languageStudio.root()} component={LanguageStudioRoutes} />
        ),
        <Route key="luis" path={routeTable.root()} component={LuisRoutes} />
    ];

    const languageStudioRoutes = [
        <Route key="luis" path={routeTable.luis.root()} component={LuisRoutes} />,
        <Route key="languageStudio" path={routeTable.languageStudio.root()} component={LanguageStudioRoutes} />
    ];

    if (isAuthLoading) {
        /**
         * Avoids the annoying white screen cutting with the loading logo
         * when the user is signed in.
         *
         * @see authFlow.md for more information.
         */
        if (getStorage('sessionStorage').hasData(LuisConstants.StorageKeys.TenantSignInStorageKey)) {
            return <BlankPage />;
        }

        return <LoadingPage />;
    }

    return (
        <AppRoutesContentContainer grow>
            <ErrorBoundary>
                <React.Suspense fallback={<LoadingPage />}>
                    <Switch>
                        {(isDevelopmentEnvironment() || isStagingEnvironment()) && (
                            <Route key="demos" path={routeTable.demos.root()} component={DemosRoutes} />
                        )}
                        {uiStore.portal.kind === 'languageStudio' ? languageStudioRoutes : gaPortalRoutes}
                        <NotFoundRedirect />
                    </Switch>
                </React.Suspense>
            </ErrorBoundary>
        </AppRoutesContentContainer>
    );
});

Routes.displayName = 'Routes';
